import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { meetingsAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import CompanyPicker from './CompanyPicker';
import ContactsEditor from './ContactsEditor';
import AttendeesPicker from './AttendeesPicker';
import PointerQuestionsForm from './PointerQuestionsForm';
import MeetingFilesSection from './MeetingFilesSection';
import MeetingActionItemsSection from './MeetingActionItemsSection';
import MeetingNotesSection from './MeetingNotesSection';
import { listTimezones, browserTimezone, isChinaHk, toLocalInputValue, fmtDateTimeInTz, apiErrorText } from './meetingsUtils';

const PLATFORMS = ['GOOGLE_MEET', 'ZOHO_MEETING', 'ZOOM', 'TEAMS', 'WECHAT', 'WHATSAPP', 'OTHER'];
const VISIT_TYPES = [
  ['WE_VISITED', 'At their office or site'],
  ['THEY_VISITED', 'At our office'],
  ['TRADE_SHOW', 'Trade show or event'],
];
const VISIT_TYPE_LABELS = Object.fromEntries(VISIT_TYPES);
const REPEAT_OPTIONS = [
  ['NONE', "Doesn't repeat"], ['WEEKLY', 'Every week'], ['BIWEEKLY', 'Every 2 weeks'],
  ['MONTHLY', 'Every month'], ['QUARTERLY', 'Every quarter'],
];

// A default of exactly "now" is a guaranteed 400 for a Plan ("must be in the future") — round
// up to the next half hour and push an hour out so the default value is always valid to save.
function defaultPlanStart() {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  const mins = d.getMinutes();
  d.setSeconds(0, 0);
  d.setMinutes(mins <= 30 ? 30 : 0, 0, 0);
  if (mins > 30) d.setHours(d.getHours() + 1);
  return d;
}

export default function MeetingCreateModal({ open, onClose, onCreated }) {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  // Change A (26 Oct 2026, Lex's later round): Plan vs Log must be an explicit, visible
  // choice — not an implicit "future date = planned" guess the user never saw.
  const [intent, setIntent] = useState('PLAN');
  const [mode, setMode] = useState('PHYSICAL');
  const [company, setCompany] = useState(null);
  const [discussedAs, setDiscussedAs] = useState('CUSTOMER');
  const [partyTypeTouched, setPartyTypeTouched] = useState(false);
  const [visitType, setVisitType] = useState('WE_VISITED');
  const [eventName, setEventName] = useState('');
  const [platform, setPlatform] = useState('ZOOM');
  const [purposes, setPurposes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [startAtLocal, setStartAtLocal] = useState(toLocalInputValue(defaultPlanStart()));
  const [tz, setTz] = useState(browserTimezone());
  const [duration, setDuration] = useState(60);
  const [city, setCity] = useState('');
  const [locationNote, setLocationNote] = useState('');
  const [meetingLink, setMeetingLink] = useState('');
  const [wechatGroup, setWechatGroup] = useState('');
  // Change C: attendees — the logger (organiser) is included by default via "I attended".
  const [attendeeUserIds, setAttendeeUserIds] = useState(user?.id ? [user.id] : []);
  const [attendeeVisitorIds, setAttendeeVisitorIds] = useState([]);
  const [attendeeNamesUntagged, setAttendeeNamesUntagged] = useState([]);
  // Change E: recurring visits — only meaningful while Planning.
  const [repeat, setRepeat] = useState('NONE');
  const [repeatUntil, setRepeatUntil] = useState('');
  const [saving, setSaving] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [duplicateAction, setDuplicateAction] = useState('draft');

  // Part A1 (26 Oct 2026, Lex's Preview feedback): "Log a meeting that happened" is one
  // continuous form — details + the full report together — with Save draft / Submit.
  // `draftMeeting` is the backend record once it exists; before that everything above
  // is still editable, after that the details are locked and only the report, files and
  // actions keep changing (matching what the backend will actually let a PUT touch).
  const [draftMeeting, setDraftMeeting] = useState(null);
  const [summary, setSummary] = useState('');
  const [summaryCn, setSummaryCn] = useState('');
  const [rating, setRating] = useState('');
  const [answers, setAnswers] = useState({});
  const [details, setDetails] = useState({});
  const [missing, setMissing] = useState([]);
  const [sendReportEmail, setSendReportEmail] = useState(true);

  useEffect(() => {
    if (open) meetingsAPI.getSettings().then((r) => setSettings(r.data)).catch(() => {});
  }, [open]);
  useEffect(() => { if (open && user?.id) setAttendeeUserIds((ids) => (ids.includes(user.id) ? ids : [user.id, ...ids])); }, [open, user]);

  // A fresh open must not reuse a previous Log draft's state.
  useEffect(() => {
    if (!open) {
      setDraftMeeting(null); setSummary(''); setSummaryCn(''); setRating(''); setAnswers({}); setDetails({});
      setMissing([]); setSendReportEmail(true); setDuplicateWarning(null); setPartyTypeTouched(false);
      setAttendeeNamesUntagged([]);
    }
  }, [open]);
  // A stale "Log anyway" offer must not survive the user changing what they're logging.
  useEffect(() => { setDuplicateWarning(null); }, [company, startAtLocal, tz]);
  // Switching Plan/Log must not leave a stale date that now violates the new min/max.
  useEffect(() => { setStartAtLocal(toLocalInputValue(intent === 'PLAN' ? defaultPlanStart() : new Date())); }, [intent]);
  // Round 2 (31 Oct 2026, party-type-first form): pre-fill only — never overwrite a
  // party type the user already picked themselves. A PROSPECT is the one exception —
  // the backend always locks it to the saved potential_type (set once at creation,
  // never re-asked per meeting), so the toggle must follow it and stays disabled below.
  useEffect(() => {
    if (company?.type === 'PROSPECT') { setDiscussedAs(company.potential_type || 'CUSTOMER'); return; }
    if (partyTypeTouched) return;
    if (company?.type === 'VENDOR') setDiscussedAs('VENDOR');
    else setDiscussedAs('CUSTOMER');
  }, [company?.ref]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePurpose = (code) => setPurposes((p) => (p.includes(code) ? p.filter((x) => x !== code) : [...p, code]));
  const toAnswerArray = () => {
    const codes = new Set([...Object.keys(answers), ...Object.keys(details)]);
    return [...codes].map((code) => ({ code, value: answers[code], detail: details[code] }));
  };

  const buildCreatePayload = (force) => {
    const payload = {
      mode, intent, company: { type: company.type, ref: company.ref, country: company.country },
      purposes, contacts, start_at_local: startAtLocal, tz, duration_min: Number(duration), force,
      wechat_group_at_meeting: wechatGroup || undefined,
      attendee_user_ids: attendeeUserIds, attendee_visitor_ids: attendeeVisitorIds,
      attendee_names_untagged: attendeeNamesUntagged,
      repeat, repeat_until: repeat !== 'NONE' ? repeatUntil : undefined,
    };
    // Round 2 (31 Oct 2026): party type is now its own always-asked first question
    // and always drives counterparty_role, decoupled from the company's own type.
    payload.counterparty_role = discussedAs;
    if (mode === 'PHYSICAL') {
      payload.visit_type = visitType;
      if (visitType === 'TRADE_SHOW') payload.event_name = eventName;
      payload.city = city; payload.location_note = locationNote;
    } else {
      payload.platform = platform; payload.meeting_link = meetingLink;
    }
    return payload;
  };

  const validateTop = () => {
    if (!discussedAs) { toast.error('Please choose the party type'); return false; }
    if (!company) { toast.error('Please select the company'); return false; }
    if (intent === 'PLAN' && repeat !== 'NONE' && !repeatUntil) { toast.error('Please choose a repeat-until date'); return false; }
    return true;
  };

  // Plan: unchanged single-step save — no report, nothing more to fill in here.
  const submitPlan = async (force = false) => {
    if (!validateTop()) return;
    setSaving(true);
    try {
      const res = await meetingsAPI.create(buildCreatePayload(force));
      toast.success('Meeting planned');
      setDuplicateWarning(null);
      onCreated(res.data);
    } catch (e) {
      if (e.response?.status === 409) { setDuplicateAction('plan'); setDuplicateWarning(e.response.data.detail.organiser_name || 'A colleague'); }
      else { setDuplicateWarning(null); toast.error(apiErrorText(e, 'Failed to save meeting')); }
    } finally { setSaving(false); }
  };

  const reportFields = () => ({
    summary, summary_cn: summaryCn, relationship_rating: rating ? Number(rating) : undefined,
    wechat_group_at_meeting: wechatGroup || undefined, answers: toAnswerArray(), contacts,
    // Attendees/purposes stay editable after the draft exists, so they must be saved too.
    purposes, attendee_user_ids: attendeeUserIds, attendee_visitor_ids: attendeeVisitorIds,
  });

  // Log, Save draft: creates the record the first time (details + whatever report
  // fields are filled in so far), then just keeps saving the report on the same record.
  const saveDraft = async (force = false) => {
    if (!validateTop()) return;
    setSaving(true);
    try {
      let m = draftMeeting;
      if (!m) {
        const created = (await meetingsAPI.create(buildCreatePayload(force))).data;
        setDraftMeeting(created); // set immediately — a failed follow-up update must not cause a second create
        m = (await meetingsAPI.update(created.id, reportFields())).data;
      } else {
        m = (await meetingsAPI.update(m.id, reportFields())).data;
      }
      setDraftMeeting(m);
      toast.success('Draft saved');
      setDuplicateWarning(null);
    } catch (e) {
      if (e.response?.status === 409 && !draftMeeting) { setDuplicateAction('draft'); setDuplicateWarning(e.response.data.detail.organiser_name || 'A colleague'); }
      else toast.error(apiErrorText(e, 'Failed to save draft'));
    } finally { setSaving(false); }
  };

  // Log, Submit: ensures the record exists and is up to date, then submits it for review.
  const submitLog = async (force = false) => {
    if (!validateTop()) return;
    setSaving(true); setMissing([]);
    try {
      let m = draftMeeting;
      if (!m) {
        const created = (await meetingsAPI.create(buildCreatePayload(force))).data;
        setDraftMeeting(created); // set immediately — a failed follow-up update must not cause a second create
        m = (await meetingsAPI.update(created.id, reportFields())).data;
        setDraftMeeting(m);
      } else {
        m = (await meetingsAPI.update(m.id, reportFields())).data;
        setDraftMeeting(m);
      }
      const subRes = await meetingsAPI.submit(m.id, { ...reportFields(), relationship_rating: Number(rating),
        send_report_email: sendReportEmail, email_to_user_ids: sendReportEmail ? attendeeUserIds : undefined });
      toast.success('Meeting logged and submitted for review');
      setDuplicateWarning(null);
      onCreated(subRes.data);
    } catch (e) {
      if (e.response?.status === 409 && !draftMeeting) { setDuplicateAction('submit'); setDuplicateWarning(e.response.data.detail.organiser_name || 'A colleague'); }
      else {
        const detail = e.response?.data?.detail;
        if (detail && typeof detail === 'object' && detail.missing) { setMissing(detail.missing); toast.error('Please complete the report before submitting'); }
        else toast.error(apiErrorText(e, 'Failed to submit'));
      }
    } finally { setSaving(false); }
  };

  const logAnyway = () => {
    if (duplicateAction === 'plan') return submitPlan(true);
    if (duplicateAction === 'submit') return submitLog(true);
    return saveDraft(true);
  };

  const showChinaHint = isChinaHk(company?.country);
  const locked = intent === 'LOG' && !!draftMeeting;
  // Once the draft exists, trust the backend-resolved counterparty_role (authoritative for prospects).
  const syntheticMeeting = { counterparty_role: draftMeeting?.counterparty_role || discussedAs, purposes, mode };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="meeting-create-modal">
        <DialogHeader><DialogTitle>{intent === 'LOG' ? 'Log a Meeting' : 'Plan a Meeting'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {/* Round 2 (31 Oct 2026, Visits & Meetings party-type-first form): always the
              very first question, decoupled from the company's own type — a known pure
              Vendor/Customer company still pre-fills it as a convenience (see the useEffect
              below) but never forces or hides it. */}
          {!locked && (
            <div>
              <Label>This meeting was with the party as</Label>
              <div className="flex gap-1.5 mt-1">
                {[['CUSTOMER', 'Customer'], ['VENDOR', 'Vendor'], ['BOTH', 'Both']].map(([v, l]) => (
                  <button key={v} type="button" disabled={company?.type === 'PROSPECT'}
                    onClick={() => { setDiscussedAs(v); setPartyTypeTouched(true); }} data-testid={`meeting-party-type-${v}`}
                    className={`px-3 py-1.5 text-sm rounded-sm border disabled:opacity-50 disabled:cursor-not-allowed ${discussedAs === v ? 'bg-foreground text-background border-foreground' : 'bg-background'}`}>
                    {l}
                  </button>
                ))}
              </div>
              {company?.type === 'PROSPECT' && (
                <p className="text-xs text-muted-foreground mt-1" data-testid="meeting-party-type-prospect-hint">
                  Set when this prospect was created — change it from the company's profile instead.
                </p>
              )}
            </div>
          )}
          {locked ? (
            <div className="rounded-sm border bg-muted/30 p-3 text-sm" data-testid="meeting-locked-summary">
              <p className="font-medium">{draftMeeting.company?.name_at_meeting}</p>
              <p className="text-muted-foreground">
                With the party as: {draftMeeting.counterparty_role === 'BOTH' ? 'Vendor & Customer' : draftMeeting.counterparty_role?.charAt(0) + draftMeeting.counterparty_role?.slice(1).toLowerCase()}
              </p>
              <p className="text-muted-foreground">
                {fmtDateTimeInTz(draftMeeting.start_at, draftMeeting.tz)} ({draftMeeting.tz}) · {draftMeeting.duration_min} min ·{' '}
                {draftMeeting.mode === 'PHYSICAL' ? (VISIT_TYPE_LABELS[draftMeeting.visit_type] || draftMeeting.visit_type) : (draftMeeting.platform || '').replace(/_/g, ' ')}
              </p>
            </div>
          ) : (
            <>
              {/* Change A: Plan vs Log — a visible two-button toggle, not a hidden guess */}
              <div>
                <Label>What are you doing?</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <button type="button" data-testid="meeting-intent-plan-btn" onClick={() => setIntent('PLAN')}
                    className={`rounded-sm border px-3 py-2 text-sm font-medium transition-colors ${intent === 'PLAN' ? 'bg-foreground text-background border-foreground' : 'bg-background hover:bg-muted/60'}`}>
                    Plan a future meeting
                  </button>
                  <button type="button" data-testid="meeting-intent-log-btn" onClick={() => setIntent('LOG')}
                    className={`rounded-sm border px-3 py-2 text-sm font-medium transition-colors ${intent === 'LOG' ? 'bg-foreground text-background border-foreground' : 'bg-background hover:bg-muted/60'}`}>
                    Log a meeting that happened
                  </button>
                </div>
              </div>

              {/* Change A: Mode as a visible two-button toggle, not a dropdown */}
              <div>
                <Label>Mode</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <button type="button" data-testid="meeting-mode-physical-btn" onClick={() => setMode('PHYSICAL')}
                    className={`rounded-sm border px-3 py-2 text-sm font-medium transition-colors ${mode === 'PHYSICAL' ? 'bg-foreground text-background border-foreground' : 'bg-background hover:bg-muted/60'}`}>
                    In person
                  </button>
                  <button type="button" data-testid="meeting-mode-virtual-btn" onClick={() => setMode('VIRTUAL')}
                    className={`rounded-sm border px-3 py-2 text-sm font-medium transition-colors ${mode === 'VIRTUAL' ? 'bg-foreground text-background border-foreground' : 'bg-background hover:bg-muted/60'}`}>
                    Virtual
                  </button>
                </div>
              </div>

              <CompanyPicker value={company} onChange={setCompany} label="Company" testIdPrefix="main-company" />

              {mode === 'PHYSICAL' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Visit type</Label>
                    <Select value={visitType} onValueChange={setVisitType}>
                      <SelectTrigger data-testid="meeting-visit-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{VISIT_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {visitType === 'TRADE_SHOW' ? (
                    <div><Label>Event name</Label><Input value={eventName} onChange={(e) => setEventName(e.target.value)} data-testid="meeting-event-name-input" /></div>
                  ) : (
                    <div><Label>City</Label><Input value={city} onChange={(e) => setCity(e.target.value)} data-testid="meeting-city-input" /></div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Platform</Label>
                    <Select value={platform} onValueChange={setPlatform}>
                      <SelectTrigger data-testid="meeting-platform-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Meeting link (optional)</Label><Input value={meetingLink} onChange={(e) => setMeetingLink(e.target.value)} data-testid="meeting-link-input" /></div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2">
                  <Label>Date & start time {intent === 'PLAN' ? '(must be in the future)' : '(must be in the past)'}</Label>
                  <Input type="datetime-local" value={startAtLocal} onChange={(e) => setStartAtLocal(e.target.value)}
                    min={intent === 'PLAN' ? toLocalInputValue(new Date()) : undefined}
                    max={intent === 'LOG' ? toLocalInputValue(new Date()) : undefined}
                    data-testid="meeting-start-at-input" />
                </div>
                <div><Label>Duration (min)</Label><Input type="number" value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="meeting-duration-input" /></div>
              </div>
              <div>
                <Label>Timezone</Label>
                <Select value={tz} onValueChange={setTz}>
                  <SelectTrigger data-testid="meeting-tz-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">{listTimezones().map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              {/* Change E: recurring visits — only while planning ahead */}
              {intent === 'PLAN' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Repeat</Label>
                    <Select value={repeat} onValueChange={setRepeat}>
                      <SelectTrigger data-testid="meeting-repeat-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{REPEAT_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {repeat !== 'NONE' && (
                    <div><Label>Repeat until</Label><Input type="date" value={repeatUntil} onChange={(e) => setRepeatUntil(e.target.value)} data-testid="meeting-repeat-until-input" /></div>
                  )}
                </div>
              )}
            </>
          )}

          {showChinaHint && (
            <div>
              <Label>WeChat group name <span className="text-muted-foreground text-xs">(required before submitting — China/Hong Kong)</span></Label>
              <Input value={wechatGroup} onChange={(e) => setWechatGroup(e.target.value)} data-testid="meeting-wechat-group-input" />
            </div>
          )}

          <AttendeesPicker selfId={user?.id} selfName={user?.name} userIds={attendeeUserIds} visitorIds={attendeeVisitorIds}
            namesUntagged={attendeeNamesUntagged} onChangeUserIds={setAttendeeUserIds} onChangeVisitorIds={setAttendeeVisitorIds}
            onChangeNamesUntagged={setAttendeeNamesUntagged} allowTypedName={!locked} />

          {settings && (
            <div>
              <Label className="block">Purpose(s)</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {settings.purposes.map((p) => (
                  <label key={p.code} className="flex items-center gap-1.5 border rounded-sm px-2 py-1 text-sm cursor-pointer" data-testid={`purpose-${p.code}`}>
                    <Checkbox checked={purposes.includes(p.code)} onCheckedChange={() => togglePurpose(p.code)} />
                    {p.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          {company && <ContactsEditor value={contacts} onChange={setContacts} companyType={company.type} companyRef={company.ref} />}

          {/* Part A1: for a past meeting, the full report lives in this same form. */}
          {intent === 'LOG' && (
            <div className="space-y-4 border-t pt-4">
              <h3 className="font-semibold text-sm">Report</h3>
              <div>
                <Label>Summary</Label>
                <Textarea value={summary} onChange={(e) => setSummary(e.target.value)} className="h-24" data-testid="report-summary-input" />
                <p className="text-xs text-muted-foreground">{summary.length} / {settings?.min_summary_chars || 80} min characters</p>
              </div>
              <div>
                <Label>Summary (Chinese, optional)</Label>
                <Textarea value={summaryCn} onChange={(e) => setSummaryCn(e.target.value)} data-testid="report-summary-cn-input" />
              </div>
              <div className="w-48">
                <Label>Relationship rating (1-5)</Label>
                <Select value={String(rating || '')} onValueChange={(v) => setRating(v)}>
                  <SelectTrigger data-testid="report-rating-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {settings && (
                <PointerQuestionsForm questions={settings.questions} meeting={syntheticMeeting} answers={answers} details={details}
                  settings={settings} onAnswer={(c, v) => setAnswers({ ...answers, [c]: v })} onDetail={(c, v) => setDetails({ ...details, [c]: v })} />
              )}
              {draftMeeting && (
                <>
                  <MeetingFilesSection meetingId={draftMeeting.id} files={draftMeeting.files} canEdit isVendorMeeting={company?.type === 'VENDOR'}
                    onChanged={() => meetingsAPI.get(draftMeeting.id).then((r) => setDraftMeeting(r.data)).catch(() => {})} />
                  <MeetingActionItemsSection meetingId={draftMeeting.id} organiserId={user?.id} organiserName={user?.name} canAdd />
                  <MeetingNotesSection meetingId={draftMeeting.id} notes={draftMeeting.notes}
                    onChanged={() => meetingsAPI.get(draftMeeting.id).then((r) => setDraftMeeting(r.data)).catch(() => {})} />
                </>
              )}
              {missing.length > 0 && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm p-3" data-testid="submit-missing-fields">
                  <p className="font-medium flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" /> Please complete before submitting:</p>
                  <ul className="list-disc ml-5 mt-1">{missing.map((m, i) => <li key={i}>{m}</li>)}</ul>
                </div>
              )}
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="send-report-email-checkbox-label">
                <input type="checkbox" checked={sendReportEmail} onChange={(e) => setSendReportEmail(e.target.checked)} data-testid="send-report-email-checkbox" />
                Email this report to the attendees on submit
              </label>
            </div>
          )}

          {duplicateWarning && (
            <div className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded-sm p-3" data-testid="duplicate-meeting-warning">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span className="flex-1">{duplicateWarning} already has a meeting with this company on that date.</span>
              <Button size="sm" variant="outline" disabled={saving} onClick={logAnyway} data-testid="duplicate-log-anyway-btn">Log anyway</Button>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose} data-testid="meeting-create-cancel-btn">Cancel</Button>
            {intent === 'LOG' ? (
              <>
                <Button variant="outline" onClick={() => saveDraft(false)} disabled={saving} data-testid="meeting-save-draft-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Save draft
                </Button>
                <Button onClick={() => submitLog(false)} disabled={saving} data-testid="meeting-submit-log-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Submit
                </Button>
              </>
            ) : (
              <Button onClick={() => submitPlan(false)} disabled={saving} data-testid="meeting-create-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Save
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
